import BaseService from '../../base/base_service';
import _ from '../../utils/underscore';
import { IAbTestingData } from "./ab_testing_service_interface";
import { SYSTEM_EVENTS } from '../../events/events';
import IAbTestingService from './ab_testing_service_interface';
import { IAbTestingFeatureProperties } from './ab_testing_service_interface';
import Store from '../../utils/store';
import Device from '../../utils/device/device';

const AB_TESTING_DATA_LOCAL_STORAGE_KEY = "ABTestingDataForUser";
const AB_TESTING_ANONYMOUS_PLAYER_ID_LOCAL_STORAGE_KEY = "ABTestingAnonymousPlayerId";

interface AbTestingServiceParams {
    enableABFiltering: true;
    isAnonymousPlayerSupportEnabled: boolean;
    getTestsForAnonymousPlayerUrl: string;
}


export default class AbTestingService extends BaseService implements IAbTestingService {

    static CLASS = 'AbTestingService';

    private _params: AbTestingServiceParams;

    private _cachedABTestingData: IAbTestingData;

    private _anonymousPlayerId: string = null;

    private _anonymousPlayerData: IAbTestingData = {
        ErrorDescription: "",
        Features: {},
        AnonymousPlayerId: null
    };

    public init(params: AbTestingServiceParams): void {
        super.init(params);
		this._params = params;
        this._resetCache();
        this._subscribe();
    }

    onInitializationCompleted(): Promise<any> {
        this._readAbTestingDataFromToken();
        this._updateAnonymousAbTestingData();
        return super.onInitializationCompleted();
    }

    private _subscribe(): void {
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
            this._resetCacheData();
        });
    }

    private _resetCacheData(): void {
        this._resetCache();
        this._readAbTestingDataFromToken();
    }

    private _resetCache(): void {
        this._cachedABTestingData = {
            ErrorDescription: null,
            Features: {},
            AnonymousPlayerId: null
        };
    }

    getAnonymousPlayerId(): string {
        return this._anonymousPlayerId;
    }

    getAbTestingDataForFeature(featureName: string): IAbTestingFeatureProperties {
        if (this._cachedABTestingData?.Features) {
            const feature: IAbTestingFeatureProperties = this._cachedABTestingData.Features[featureName];
            if (feature?.Properties) {
                if(feature.Properties.featureToggle){
                    const toggle: string = feature.Properties.featureToggle.toString().toLowerCase();
                    if(toggle === "false") return null;
                }
                return feature;
            }
        }
        return null;
    }

    getAnonymousPlayerAbTestingDataForFeature(featureName: string): IAbTestingFeatureProperties {
        if (this._anonymousPlayerData?.Features) {
            const feature: IAbTestingFeatureProperties = this._cachedABTestingData.Features[featureName];
            if (feature?.Properties) {
                if(feature.Properties.featureToggle){
                    const toggle: string = feature.Properties.featureToggle.toString().toLowerCase();
                    if(toggle === "false") return null;
                }
                return feature;
            }
        }
        return null;
    }

    private async _updateAnonymousAbTestingData(): Promise<void> {
        if (!this._params.isAnonymousPlayerSupportEnabled || this.serviceFactory.oAuth2Service().isAuthorized()) {
            return;
        }

        if (!this._anonymousPlayerId) {
            this._anonymousPlayerId = this._getLocalStorageAnonymousPlayerId();
        }

        try {
            const request: any = {
                brandId: this.serviceFactory.skinService().getBrandID(),
                subBrandId: this.serviceFactory.skinService().getSubBrandID(),
                countryCode: this.serviceFactory.countryService().getCountry(),
                isMobile: Device.os.isMobileOS
            };
            if (this._anonymousPlayerId) {
                request.anonymousPlayerId = this._anonymousPlayerId;
            }
            this._anonymousPlayerData = await this.serviceFactory.http2Service().post<IAbTestingData>(this._params.getTestsForAnonymousPlayerUrl, request);
            if (!this._anonymousPlayerId && this._anonymousPlayerData?.AnonymousPlayerId) {
                //we got new anonymous id to save and store
                this._anonymousPlayerId = this._anonymousPlayerData.AnonymousPlayerId;
                this._saveLocalStorageAnonymousPlayerId();
            }
        }
        catch (err) {
            this.logger.error(`AbTestingService.FetTestsForAnonymousPlayer failed`, err);
        }
    }

    getAbTestingData(): { [key: string]: IAbTestingFeatureProperties } {
        if (this._cachedABTestingData?.Features && !this._cachedABTestingData.ErrorDescription) {
            const result: { [key: string]: IAbTestingFeatureProperties } = {};
            for (const featureName in this._cachedABTestingData.Features) {
                result[featureName] = this.getAbTestingDataForFeature(featureName);
            }
            return result;
        } else {
            return null;
        }
    }

    private _readAbTestingDataFromToken(): void {
        const abTestingDataTokenProperty = this.serviceFactory.oAuth2Service().extractTokenProperty('abTestingData');
        if (abTestingDataTokenProperty) {
            this._cachedABTestingData = _.safeJSON(abTestingDataTokenProperty);
            this._saveLocalStorageABTestingData();
            if (this._cachedABTestingData.AnonymousPlayerId &&
                this._cachedABTestingData.AnonymousPlayerId !== this._anonymousPlayerId
            ) {
                if (this._anonymousPlayerId) {
                    this.logger.warn(`For user ${this.serviceFactory.userService().getCID()} existing anonymous id ${this._anonymousPlayerId} was replaced by token anonymous id ${this._cachedABTestingData.AnonymousPlayerId}`)
                } else {
                    this.logger.debug(`For user ${this.serviceFactory.userService().getCID()} anonymous id was set with token anonymous id ${this._cachedABTestingData.AnonymousPlayerId}`)
                }
                this._anonymousPlayerId = this._cachedABTestingData.AnonymousPlayerId;
                this._saveLocalStorageAnonymousPlayerId();
            }
        } else {
            const localStorageABTestingData = this._getLocalStorageABTestingData();
            if (localStorageABTestingData) {
                this._cachedABTestingData = localStorageABTestingData;
            }
        }
    }

    private _saveLocalStorageABTestingData(): void {
        const abTestingData = this._cachedABTestingData;
        if (abTestingData && !abTestingData.ErrorDescription) {
          Store.set(AB_TESTING_DATA_LOCAL_STORAGE_KEY, JSON.stringify(abTestingData));
        }
    }

    private _getLocalStorageAnonymousPlayerId(): string {
        return Store.get(AB_TESTING_ANONYMOUS_PLAYER_ID_LOCAL_STORAGE_KEY, null);
    }

    private _saveLocalStorageAnonymousPlayerId(): void {
        Store.set(AB_TESTING_ANONYMOUS_PLAYER_ID_LOCAL_STORAGE_KEY, this._anonymousPlayerId);
    }

    private _getLocalStorageABTestingData(): IAbTestingData {
        return JSON.parse(Store.get(AB_TESTING_DATA_LOCAL_STORAGE_KEY, null));
    }
}
